import { useState, useEffect } from "react";
import { Alert, Button, Box, Stack } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useFeature } from "../hooks";

const VERSION_CHECK_INTERVAL = 30000;
const LOCAL_STORAGE_KEY = "app_version";
const DISMISSED_KEY = "app_version_dismissed";

export default function VersionBanner() {
  const { t } = useTranslation(["translation", "components"]);
  const [showBanner, setShowBanner] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);

  const bannerEnabled = useFeature("dispatcher-app-version-banner");

  useEffect(() => {
    // Initial version fetch and store
    const initializeVersion = async () => {
      try {
        const response = await axios.get("/api/version");
        const serverVersion = response.data.version;

        const storedVersion = localStorage.getItem(LOCAL_STORAGE_KEY);
        const dismissedVersion = sessionStorage.getItem(DISMISSED_KEY);

        if (!storedVersion) {
          // First time loading, store the version
          localStorage.setItem(LOCAL_STORAGE_KEY, serverVersion);
          setCurrentVersion(serverVersion);
        } else if (storedVersion !== serverVersion) {
          // Version mismatch - check if user dismissed this version in this session
          if (dismissedVersion !== serverVersion) {
            setShowBanner(true);
          }
          setCurrentVersion(serverVersion);
        } else {
          // Version matches
          setCurrentVersion(serverVersion);
        }
      } catch (error) {
        console.error("Failed to fetch version:", error);
      }
    };

    initializeVersion();

    // Set up periodic version checking
    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get("/api/version");
        const serverVersion = response.data.version;
        const storedVersion = localStorage.getItem(LOCAL_STORAGE_KEY);
        const dismissedVersion = sessionStorage.getItem(DISMISSED_KEY);

        if (storedVersion && storedVersion !== serverVersion) {
          // Only show banner if user hasn't dismissed this version in this session
          if (dismissedVersion !== serverVersion) {
            setShowBanner(true);
            setCurrentVersion(serverVersion);
          }
        }
      } catch (error) {
        console.error("Failed to check version:", error);
      }
    }, VERSION_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = () => {
    if (currentVersion) {
      localStorage.setItem(LOCAL_STORAGE_KEY, currentVersion);
    }
    window.location.reload();
  };

  const handleDismiss = () => {
    if (currentVersion) {
      // Store in sessionStorage so it's dismissed for this session only
      sessionStorage.setItem(DISMISSED_KEY, currentVersion);
    }
    setShowBanner(false);
  };

  if (!bannerEnabled) {
    return null;
  }

  if (!showBanner) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
      }}
    >
      <Alert
        severity="warning"
        action={
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleDismiss}
            >
              {t("components:buttonLabels.cancel")}
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleRefresh}
            >
              {t("components:buttonLabels.refresh")}
            </Button>
          </Stack>
        }
      >
        {t("translation:newAppVersion")}
      </Alert>
    </Box>
  );
}
